import React, { FC, useEffect, useState } from 'react';
import { useUserContext } from '../../../providers/UserProvider';
import { PopUpHandler } from '../../shared/PopupHandler';
import { useTranslations } from '../../../hooks/useTranslationsHelper';
import './ProtectionOfCustomerFundsReminder.scss';
import { Button } from '../../shared/Button';
import { useUserBalance } from '../../../hooks/useUserBalance';
import { useUserProfileUpdateMutation } from '../../../libs/graphql/baseAppAPI/mutations/__generated__/user-profile-update.mutation.generated';
import { xTenantId } from '../../../consts';
import { ContentPrompt } from '../../Registration/steps/ContentPrompt';
import { Policies } from '../../Registration/types';

export const ProtectionOfCustomerFundsReminder: FC = () => {
  const { consents, fetchUserProfile, userProfileId } = useUserContext();
  const { t } = useTranslations();
  const { userBalance } = useUserBalance();
  const [modalValue, setModalValue] = useState<Policies | null>(null);

  const [showModal, setShowModal] = useState(false);

  const [userProfileUpdateMutation] = useUserProfileUpdateMutation({
    onCompleted: async (data) => {
      if (!data) return;
      await fetchUserProfile();
      setShowModal(false);
    },
    onError: (error) => {
      console.error('Error updating profile:', error);
    },
  });

  useEffect(() => {
    if (!consents) return;

    const sixMonthsAgo = new Date();

    sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);

    const lastReview = consents?.lastInsolvencyReviewOnUtc;
    const needsReview = Boolean(lastReview && new Date(lastReview) < sixMonthsAgo);

    setShowModal(needsReview);
  }, [consents]);

  const handleAcknowledge = async (): Promise<void> => {
    const currentDate = new Date().toISOString();

    await userProfileUpdateMutation({
      variables: {
        userProfileId,
        xTenantId,
        userProfileUpdateInfo: {
          lastInsolvencyReviewOnUtc: currentDate,
        },
      },
    });
  };

  return (
    <>
      <PopUpHandler
        isOpen={showModal}
        onClose={(): void => {}}
        header={t('insolvency-reminder-title')}
        modalProps={{
          'aria-label': 'Protection of Customer Funds Reminder',
          className: 'insolvency-reminder-modal',
        }}
        drawerProps={{ className: 'insolvency-reminder-drawer' }}
        showCloseBtn={false}
      >
        <div className="insolvency-reminder-content">
          <p className="insolvency-reminder__text">{t('insolvency-reminder-paragraph1')}</p>

          <p
            className="insolvency-reminder__text"
            dangerouslySetInnerHTML={{ __html: t('insolvency-reminder-paragraph2') }}
          />

          <p className="insolvency-reminder__text">
            {t('insolvency-reminder-paragraph3', {
              balance: userBalance.mainCashBalance.toFixed(2),
            })}
          </p>

          <p className="insolvency-reminder__text">{t('insolvency-reminder-paragraph4')}</p>

          <p className="insolvency-reminder__text">
            <button
              type="button"
              className="insolvency-reminder__policy-link"
              onClick={(): void => {
                setModalValue(Policies.PofPolicy);
              }}
            >
              Read Full Policy
            </button>
          </p>

          <p className="insolvency-reminder__text insolvency-reminder__text--footer">
            {t('insolvency-reminder-paragraph5')}
          </p>

          <div className="insolvency-reminder__button">
            <Button type="button" variant="primary" onClick={handleAcknowledge}>
              ACKNOWLEDGE
            </Button>
          </div>
          {modalValue && <ContentPrompt modalValue={modalValue} onClose={(): void => setModalValue(null)} />}
        </div>
      </PopUpHandler>
    </>
  );
};
